import type { TrevahhhSyncEngine } from './sync/syncEngine'

interface RateRow {
  user_id: string
  device_id: string
  limit_id: string
  limit_name: string | null
  primary_used_percent: number | null
  primary_window_minutes: number | null
  primary_resets_at: string | null
  secondary_used_percent: number | null
  secondary_window_minutes: number | null
  secondary_resets_at: string | null
  plan_type: string | null
  credits_json: unknown
  reached_type: string | null
  observed_at: string
}

interface DeviceRow {
  id: string
  device_name: string
  platform: string
  companion_version: string | null
  paired_at: string
  last_seen_at: string | null
  revoked_at: string | null
  connection_status: string
  codex_cli_version: string | null
  app_server_version: string | null
}

interface SnapshotRow {
  limit_id: string
  primary_used_percent: number | null
  secondary_used_percent: number | null
  primary_resets_at: string | null
  secondary_resets_at: string | null
  reached_type: string | null
  observed_at: string
}

interface UsageRow {
  lifetime_tokens: number | null
  peak_daily_tokens: number | null
  longest_running_turn_seconds: number | null
  current_streak_days: number | null
  longest_streak_days: number | null
  daily_usage_json: Array<{ startDate: string; tokens: number }> | null
  observed_at: string
}

interface AlertRow {
  id: string
  alert_type: string
  limit_id: string | null
  threshold: number | null
  created_at: string
  acknowledged_at: string | null
}

interface PreferenceRow {
  thresholds: number[]
  notify_in_app: boolean
  notify_browser: boolean
}

const colors: Record<string, string> = {
  ready: 'var(--green)',
  caution: 'var(--gold2)',
  low: 'var(--orange)',
  critical: 'var(--red)',
  unknown: 'var(--muted)',
  stale: 'var(--muted)',
  offline: 'var(--muted)'
}

const esc = (value: unknown): string =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[char] ?? char)

const clamp = (value: unknown): number | null => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null
  return Math.max(0, Math.min(100, Number(value)))
}

const remaining = (used: unknown): number | null => {
  const value = clamp(used)
  return value === null ? null : 100 - value
}

const fmtPct = (value: number | null | undefined): string => value === null || value === undefined ? 'Not reported' : `${Math.round(value)}%`

const fmtTime = (value: string | null | undefined): string => {
  if (!value) return 'Not reported'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Not reported' : date.toLocaleString()
}

const countdown = (value: string | null | undefined): string => {
  if (!value) return 'Not reported'
  const diff = new Date(value).getTime() - Date.now()
  if (!Number.isFinite(diff)) return 'Not reported'
  if (diff <= 0) return 'Reset due'
  const minutes = Math.ceil(diff / 60000)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}h ${minutes % 60}m`
  return `${Math.floor(hours / 24)}d ${hours % 24}h`
}

const ageMinutes = (value: string | null | undefined): number => {
  if (!value) return Infinity
  return Math.max(0, (Date.now() - new Date(value).getTime()) / 60000)
}

function windowLabel(minutes: number | null): string {
  const mins = Number(minutes)
  if (!Number.isFinite(mins) || mins <= 0) return 'UNKNOWN WINDOW'
  if (Math.abs(mins - 300) <= 5) return '5-HOUR LIMIT'
  if (Math.abs(mins - 10080) <= 5) return 'WEEKLY LIMIT'
  if (mins === 1440) return '1-DAY LIMIT'
  if (mins % 10080 === 0) return `${mins / 10080}-WEEK LIMIT`
  if (mins % 1440 === 0) return `${mins / 1440}-DAY LIMIT`
  if (mins % 60 === 0) return `${mins / 60}-HOUR LIMIT`
  return `${mins}-MINUTE LIMIT`
}

function statusFor(value: number | null, freshness: string) {
  if (freshness === 'BRIDGE OFFLINE') return { key: 'offline', label: 'BRIDGE OFFLINE' }
  if (freshness === 'STALE') return { key: 'stale', label: 'STALE' }
  if (value === null) return { key: 'unknown', label: 'UNKNOWN' }
  if (value > 40) return { key: 'ready', label: 'READY' }
  if (value > 20) return { key: 'caution', label: 'CAUTION' }
  if (value > 10) return { key: 'low', label: 'LOW' }
  return { key: 'critical', label: 'CRITICAL' }
}

function rowWindows(row: RateRow) {
  return [
    {
      type: 'primary',
      label: windowLabel(row.primary_window_minutes),
      used: clamp(row.primary_used_percent),
      remaining: remaining(row.primary_used_percent),
      resetsAt: row.primary_resets_at,
      minutes: row.primary_window_minutes
    },
    {
      type: 'secondary',
      label: windowLabel(row.secondary_window_minutes),
      used: clamp(row.secondary_used_percent),
      remaining: remaining(row.secondary_used_percent),
      resetsAt: row.secondary_resets_at,
      minutes: row.secondary_window_minutes
    }
  ].filter((window) => window.used !== null || window.resetsAt || window.minutes)
}

export class CodexFuelController {
  rows: RateRow[] = []
  devices: DeviceRow[] = []
  snapshots: SnapshotRow[] = []
  usage: UsageRow | null = null
  alerts: AlertRow[] = []
  preferences: PreferenceRow | null = null
  lastError: string | null = null
  private channel: unknown = null

  constructor(private sync: TrevahhhSyncEngine, private rerender: () => void) {}

  get canQuery(): boolean {
    return Boolean(this.sync.supabaseClient && this.sync.currentUser)
  }

  freshness(): string {
    if (!this.rows.length) return 'UNKNOWN'
    const newest = Math.min(...this.rows.map((row) => ageMinutes(row.observed_at)))
    const activeDevice = this.devices.find((device) => !device.revoked_at && ageMinutes(device.last_seen_at) <= 10)
    if (!activeDevice) return 'BRIDGE OFFLINE'
    if (newest <= 2) return 'LIVE'
    if (newest <= 10) return 'RECENT'
    if (newest <= 30) return 'STALE'
    return 'BRIDGE OFFLINE'
  }

  summary() {
    const windows = this.rows.flatMap(rowWindows)
    const values = windows.map((window) => window.remaining).filter((value): value is number => value !== null)
    const lowest = values.length ? Math.min(...values) : null
    const resetTimes = windows.map((window) => window.resetsAt).filter((value): value is string => Boolean(value)).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    const freshness = this.freshness()
    return { lowest, nearestReset: resetTimes[0] ?? null, freshness, status: statusFor(lowest, freshness), windows }
  }

  async load(): Promise<void> {
    const client = this.sync.supabaseClient
    const user = this.sync.currentUser
    if (!client || !user) {
      this.rows = []
      this.devices = []
      this.snapshots = []
      this.usage = null
      this.lastError = null
      this.rerender()
      return
    }

    const [current, devices, snapshots, usage, alerts, preferences] = await Promise.all([
      client.from('codex_rate_limit_current').select('*').order('observed_at', { ascending: false }),
      client.from('codex_bridge_devices').select('*').order('last_seen_at', { ascending: false, nullsFirst: false }),
      client.from('codex_rate_limit_snapshots').select('*').order('observed_at', { ascending: true }).limit(160),
      client.from('codex_usage_current').select('*').order('observed_at', { ascending: false }).limit(1),
      client.from('codex_fuel_alert_events').select('*').order('created_at', { ascending: false }).limit(10),
      client.from('codex_fuel_alert_preferences').select('*').maybeSingle()
    ])
    const firstError = [current, devices, snapshots, usage, alerts, preferences].find((result) => result.error)?.error
    if (firstError) throw firstError
    this.rows = (current.data ?? []) as RateRow[]
    this.devices = (devices.data ?? []) as DeviceRow[]
    this.snapshots = (snapshots.data ?? []) as SnapshotRow[]
    this.usage = (usage.data?.[0] ?? null) as UsageRow | null
    this.alerts = (alerts.data ?? []) as AlertRow[]
    this.preferences = (preferences.data ?? null) as PreferenceRow | null
    this.lastError = null
    this.subscribe()
    this.rerender()
  }

  subscribe(): void {
    const client = this.sync.supabaseClient
    const user = this.sync.currentUser
    if (!client || !user || this.channel) return
    this.channel = client
      .channel(`codex-fuel-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'codex_rate_limit_current' }, () => void this.load().catch((error) => this.handleError(error)))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'codex_bridge_devices' }, () => void this.load().catch((error) => this.handleError(error)))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'codex_usage_current' }, () => void this.load().catch((error) => this.handleError(error)))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'codex_fuel_alert_events' }, () => void this.load().catch((error) => this.handleError(error)))
      .subscribe()
  }

  handleError(error: unknown): void {
    this.lastError = error instanceof Error ? error.message : String(error)
    this.rerender()
  }

  compactHtml(): string {
    if (!this.canQuery) {
      return `<div class="codex-compact-head"><span class="eyebrow">Codex Fuel</span><span class="pill open">Sign in</span></div>
        <div class="codex-compact-value">Unknown</div>
        <p class="desc">Connect cloud sync, then pair the Windows bridge.</p>
        <button class="btn" data-view="codex-fuel">Open Codex Fuel</button>`
    }
    const summary = this.summary()
    const color = colors[summary.status.key] ?? colors.unknown ?? 'var(--muted)'
    return `<div class="codex-compact-head"><span class="eyebrow">Codex Fuel</span><span class="pill ${summary.status.key === 'critical' ? 'blocked' : summary.status.key === 'ready' ? 'done' : 'open'}">${esc(summary.status.label)}</span></div>
      <div class="codex-compact-value" style="color:${color}">${fmtPct(summary.lowest)}</div>
      <p class="desc">${esc(summary.freshness)} - nearest reset ${esc(countdown(summary.nearestReset))}</p>
      ${summary.status.key === 'critical' ? '<div class="notice">Codex Fuel is critical. Review before starting Codex-heavy work.</div>' : ''}
      <button class="btn primary" data-view="codex-fuel">Open Codex Fuel</button>`
  }

  pageHtml(): string {
    if (this.lastError) return `<div class="notice">Codex Fuel could not load: ${esc(this.lastError)}</div>`
    const summary = this.summary()
    const color = colors[summary.status.key] ?? colors.unknown ?? 'var(--muted)'
    return `<div class="pagehead"><div><h2>Codex Fuel</h2><p>Official Codex App Server usage and rate-limit telemetry from the paired Windows bridge.</p></div></div>
      <div class="layout2">
        <div class="card section hero-gate">
          <div class="eyebrow">Official measured values only</div>
          <h3>${this.canQuery ? `${esc(summary.status.label)} - ${fmtPct(summary.lowest)} remaining` : 'Sign in to connect Codex Fuel'}</h3>
          <div class="codex-hero-grid">
            ${this.gaugeHtml(summary.lowest, summary.status.key, summary.status.label)}
            <div class="today-list">
              <div class="action"><div class="num">F</div><div><strong>${esc(summary.freshness)}</strong><small>Data freshness state</small></div><span class="pill ${summary.freshness === 'LIVE' ? 'done' : 'open'}">${esc(summary.freshness)}</span></div>
              <div class="action"><div class="num">R</div><div><strong>${esc(countdown(summary.nearestReset))}</strong><small>Nearest reported reset</small></div></div>
              <div class="action"><div class="num">B</div><div><strong>${this.rows.length} bucket${this.rows.length === 1 ? '' : 's'}</strong><small>Multiple buckets are shown separately.</small></div></div>
            </div>
          </div>
        </div>
        <div class="card section">
          <h3>Bridge Pairing</h3><p class="desc">Create a short-lived one-time code for the Windows companion.</p>
          ${this.pairingHtml()}
        </div>
      </div>
      <div class="card section" style="margin-top:16px"><h3>Rate-Limit Buckets</h3><p class="desc">Missing official fields stay marked as not reported.</p><div class="cards">${this.rateLimitCardsHtml(color)}</div></div>
      <div class="layout2">
        <div class="card section"><h3>Usage Intelligence</h3><p class="desc">Shown only when Codex reports account usage.</p>${this.usageHtml()}</div>
        <div class="card section"><h3>History</h3><p class="desc">Periodic snapshots retained by the database retention rule.</p>${this.historyHtml()}</div>
      </div>
      <div class="layout2">
        <div class="card section"><h3>Paired Devices</h3><p class="desc">Revocation prevents further writes immediately.</p><div class="today-list">${this.devicesHtml()}</div></div>
        <div class="card section"><h3>Alerts</h3><p class="desc">Threshold and bridge health alert controls.</p>${this.alertsHtml()}</div>
      </div>`
  }

  private gaugeHtml(value: number | null, statusKey: string, statusLabel: string): string {
    const bounded = value ?? 0
    const angle = -84 + Math.max(0, Math.min(100, bounded)) / 100 * 168
    const color = colors[statusKey] ?? colors.unknown ?? 'var(--muted)'
    return `<div class="codex-gauge" style="--needle:${angle}deg;--fuel-color:${color}">
      <div class="codex-gauge-arc"></div><div class="codex-needle"></div>
      <div class="codex-gauge-center"><b>${fmtPct(value)}</b><span>${esc(statusLabel)}</span></div>
      <div class="codex-scale"><span>Empty</span><span>Full</span></div>
    </div>`
  }

  private pairingHtml(): string {
    if (!this.canQuery) return '<div class="notice gold">Sign in to Cloud Sync before pairing a Windows bridge.</div>'
    return '<button class="btn primary" data-action="codex-pair">Connect Windows Bridge</button><div id="codexPairingResult" style="margin-top:12px"></div>'
  }

  private rateLimitCardsHtml(color: string): string {
    if (!this.rows.length) return '<div class="empty">No official Codex rate-limit snapshot has been published yet.</div>'
    return this.rows.map((row) => `<div class="card item-card" style="--fuel-color:${color}">
      <div class="eyebrow">${esc(row.limit_id || 'codex')}</div><h4>${esc(row.limit_name || row.limit_id || 'Codex')}</h4>
      <p>Plan: ${esc(row.plan_type || 'Not reported')} - reached: ${esc(row.reached_type || 'Not reported')}</p>
      ${rowWindows(row).map((window) => `<div class="codex-window">
        <div class="codex-window-row"><b>${esc(window.label)}</b><span>${fmtPct(window.remaining)} remaining</span></div>
        <div class="progress"><i style="width:${window.remaining ?? 0}%;background:var(--fuel-color)"></i></div>
        <small>${fmtPct(window.used)} used - resets ${esc(fmtTime(window.resetsAt))} - ${esc(countdown(window.resetsAt))}</small>
      </div>`).join('') || '<div class="notice gold">No primary or secondary window reported for this bucket.</div>'}
      <p>Last data timestamp: ${esc(fmtTime(row.observed_at))}</p>
    </div>`).join('')
  }

  private usageHtml(): string {
    const usage = this.usage
    const daily = Array.isArray(usage?.daily_usage_json) ? usage.daily_usage_json : []
    const max = Math.max(1, ...daily.map((item) => Number(item.tokens || 0)))
    return `<div class="metric-strip">
      <div class="metric-mini"><span>Lifetime tokens</span><b>${esc(usage?.lifetime_tokens ?? 'Not reported')}</b></div>
      <div class="metric-mini"><span>Peak daily tokens</span><b>${esc(usage?.peak_daily_tokens ?? 'Not reported')}</b></div>
      <div class="metric-mini"><span>Longest turn</span><b>${esc(usage?.longest_running_turn_seconds ?? 'Not reported')}</b></div>
      <div class="metric-mini"><span>Current streak</span><b>${esc(usage?.current_streak_days ?? 'Not reported')}</b></div>
      <div class="metric-mini"><span>Longest streak</span><b>${esc(usage?.longest_streak_days ?? 'Not reported')}</b></div>
      <div class="metric-mini"><span>Observed</span><b style="font-size:12px">${esc(fmtTime(usage?.observed_at))}</b></div>
    </div>
    <div class="chart">${daily.slice(-14).map((item) => `<div class="bar" style="height:${Math.max(5, Number(item.tokens || 0) / max * 100)}%"><b>${esc(item.tokens)}</b><span>${esc(item.startDate)}</span></div>`).join('') || '<div class="empty">Usage is not reported by this Codex App Server.</div>'}</div>`
  }

  private historyHtml(): string {
    const points = this.snapshots.map((row) => ({ at: row.observed_at, value: remaining(row.primary_used_percent ?? row.secondary_used_percent) })).filter((point): point is { at: string; value: number } => point.value !== null).slice(-60)
    if (!points.length) return '<div class="empty">History starts after the bridge publishes real snapshots.</div>'
    return `<div class="codex-history">${points.map((point) => `<i title="${esc(fmtTime(point.at))}: ${fmtPct(point.value)}" style="height:${Math.max(3, point.value)}%"></i>`).join('')}</div>
      <p class="desc">Trend is calculated from measured snapshots. Depletion projections are estimates only.</p>`
  }

  private devicesHtml(): string {
    if (!this.devices.length) return '<div class="empty">No Windows bridge has been paired yet.</div>'
    return this.devices.map((device) => `<div class="action">
      <div class="num">${device.revoked_at ? 'X' : 'OK'}</div>
      <div><strong>${esc(device.device_name)}</strong><small>${esc(device.platform)} - ${esc(device.connection_status)} - last seen ${esc(fmtTime(device.last_seen_at))}</small></div>
      ${device.revoked_at ? '<span class="pill blocked">Revoked</span>' : `<button class="btn danger" data-action="codex-revoke" data-device-id="${esc(device.id)}">Revoke</button>`}
    </div>`).join('')
  }

  private alertsHtml(): string {
    const prefs = this.preferences
    const thresholds = prefs?.thresholds || [40, 20, 10, 5]
    return `<div class="formgrid">
      <div class="field"><label>Thresholds</label><input id="codexThresholds" value="${esc(thresholds.join(', '))}"></div>
      <label class="check"><input id="codexBrowserNotify" type="checkbox" ${prefs?.notify_browser ? 'checked' : ''}><span>Browser notifications</span><span class="pill open">Optional</span></label>
      <label class="check"><input id="codexInAppNotify" type="checkbox" ${prefs?.notify_in_app !== false ? 'checked' : ''}><span>In-app alerts</span><span class="pill done">Enabled</span></label>
    </div>
    <button class="btn primary" style="margin-top:12px" data-action="codex-save-prefs">Save Alert Preferences</button>
    <div class="today-list" style="margin-top:14px">${this.alerts.map((alert) => `<div class="action"><div class="num">!</div><div><strong>${esc(alert.alert_type)}</strong><small>${esc(alert.limit_id || '')} - ${esc(fmtTime(alert.created_at))}</small></div><span class="pill ${alert.acknowledged_at ? 'done' : 'open'}">${alert.acknowledged_at ? 'Ack' : 'New'}</span></div>`).join('') || '<div class="empty">No alert events yet.</div>'}</div>`
  }

  async createPairingCode(): Promise<void> {
    const client = this.sync.supabaseClient
    const user = this.sync.currentUser
    if (!client || !user) throw new Error('Sign in to Cloud Sync first.')
    const { data, error } = await client.rpc('codex_fuel_create_pairing_code', { p_ttl_seconds: 600 })
    if (error) throw error
    const code = Array.isArray(data) ? data[0] : data
    const config = this.sync.config()
    const command = `powershell -ExecutionPolicy Bypass -File .\\bridge\\install\\Install-CodexFuelBridge.ps1 pair -SupabaseUrl "${config.supabaseUrl}" -PublishableKey "${config.supabasePublishableKey}" -Code "${code.pairing_code}"`
    const result = document.getElementById('codexPairingResult')
    if (result) {
      result.innerHTML = `<div class="notice gold"><b>Pairing code:</b> ${esc(code.pairing_code)}<br>Expires: ${esc(fmtTime(code.expires_at))}</div>
        <textarea readonly style="width:100%;min-height:92px;margin-top:10px">${esc(command)}</textarea>`
    }
  }

  async revokeDevice(id: string): Promise<void> {
    const client = this.sync.supabaseClient
    if (!client) return
    const { error } = await client.from('codex_bridge_devices').update({ revoked_at: new Date().toISOString(), connection_status: 'revoked' }).eq('id', id)
    if (error) throw error
    await this.load()
  }

  async savePreferences(): Promise<void> {
    const client = this.sync.supabaseClient
    const user = this.sync.currentUser
    if (!client || !user) return
    const thresholds = (document.getElementById('codexThresholds') as HTMLInputElement | null)?.value
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value) => Number.isFinite(value) && value >= 0 && value <= 100) ?? [40, 20, 10, 5]
    const notifyBrowser = Boolean((document.getElementById('codexBrowserNotify') as HTMLInputElement | null)?.checked)
    if (notifyBrowser && typeof Notification !== 'undefined' && Notification.permission === 'default') await Notification.requestPermission().catch(() => undefined)
    const { error } = await client.from('codex_fuel_alert_preferences').upsert({
      user_id: user.id,
      thresholds: thresholds.length ? thresholds : [40, 20, 10, 5],
      notify_browser: notifyBrowser,
      notify_in_app: Boolean((document.getElementById('codexInAppNotify') as HTMLInputElement | null)?.checked),
      updated_at: new Date().toISOString()
    })
    if (error) throw error
    await this.load()
  }
}
